import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Send, Eye, EyeOff, RefreshCw, Search, Award } from "lucide-react";
import { apiPost } from "../../Api/Client.js";
import { alreadySent, sendFingerprint } from "./sendOnce.js";

export function SendCertificateScreen({ data, reload }) {
  const employees = data.employees || [];
  const certTemplates = (data.genericHtmlTemplates || []).filter(
    (t) => t.category === "Certificate"
  );
  const certTemplate = certTemplates[0] || null;

  const [query, setQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [joiningDate, setJoiningDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [position, setPosition] = useState("");
  const [responsibility, setResponsibility] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // What was last sent. The same details cannot be sent twice; changing any of them lifts the block.
  const [sentFingerprint, setSentFingerprint] = useState(null);

  const filtered = useMemo(() => {
    if (!query) return employees.slice(0, 50);
    const q = query.toLowerCase();
    return employees
      .filter((e) => {
        const haystack = `${e.display_name || ""} ${e.candidate_name || ""} ${e.candidate_email || ""} ${e.username || ""} ${e.employee_code || ""}`.toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 50);
  }, [employees, query]);

  const handleSelectEmployee = (id) => {
    if (!id) {
      setSelectedEmployee(null);
      return;
    }
    const emp = employees.find((e) => String(e.id) === String(id));
    setSelectedEmployee(emp || null);
    if (emp) {
      if (!position) setPosition(emp.position_title || "");
      if (!joiningDate && emp.joined_on) {
        const d = new Date(emp.joined_on);
        if (!isNaN(d)) setJoiningDate(d.toISOString().split("T")[0]);
      }
    }
  };

  const previewVars = useMemo(() => {
    const name = selectedEmployee?.display_name || selectedEmployee?.candidate_name || "";
    return {
      title: "Mr./Ms.",
      name,
      joining_date: joiningDate,
      completion_date: completionDate,
      position: position || selectedEmployee?.position_title || "",
      pronoun: "his/her",
      pronoun2: "him/her",
      pronoun3: "he/she",
      responsibility: responsibility || "",
    };
  }, [selectedEmployee, joiningDate, completionDate, position, responsibility]);

  const doPreview = async () => {
    if (!certTemplate) {
      setResult({ ok: false, message: "No Certificate Template Found. Seed Templates First." });
      return;
    }
    if (!selectedEmployee) {
      setResult({ ok: false, message: "Please Select An Employee First." });
      return;
    }
    setResult(null);
    try {
      const resp = await apiPost(
        `/HtmlTemplate/GenericHtmlTemplates/${certTemplate.id}/render/`,
        { variables: previewVars }
      );
      if (resp?.rendered) {
        setPreviewHtml(resp.rendered);
        setShowPreview(true);
      } else {
        setResult({ ok: false, message: "Failed To Render Preview." });
      }
    } catch (err) {
      setResult({
        ok: false,
        message: err?.payload?.detail || err?.message || "Preview Failed.",
      });
    }
  };

  const validate = () => {
    if (!selectedEmployee) return "Please Select An Employee.";
    if (!selectedEmployee.user) return "This Employee Has No User Account. Cannot Send Certificate.";
    if (!joiningDate) return "Joining Date Is Required.";
    if (!completionDate) return "Completion Date Is Required.";
    if (!position) return "Position Is Required.";
    return null;
  };
  const validationError = validate();
  const currentFingerprint = sendFingerprint([selectedEmployee?.id, joiningDate, completionDate, position, responsibility]);
  const repeatSend = alreadySent(sentFingerprint, currentFingerprint);

  const sendCertificate = async () => {
    if (validationError) {
      setResult({ ok: false, message: validationError });
      return;
    }
    if (repeatSend) return;
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        recipient: selectedEmployee.user,
        joining_date: joiningDate,
        completion_date: completionDate,
        position: position,
        responsibility: responsibility || "",
        title: `Internship Completion Certificate For ${selectedEmployee.display_name || selectedEmployee.candidate_name}`,
      };
      const resp = await apiPost("/MainApp/send-certificate", payload);
      if (resp?.certificate_sent || resp?.recipient_email) {
        setSentFingerprint(currentFingerprint);
        const certLabel = resp?.certificate_id ? ` Certificate #${resp.certificate_id}` : "";
        setResult({
          ok: true,
          message: `Certificate${certLabel} Sent To ${selectedEmployee.display_name || selectedEmployee.candidate_name} (${resp.recipient_email || selectedEmployee.candidate_email || ""}).`,
        });
      } else {
        setResult({ ok: false, message: "Failed To Send Certificate." });
      }
    } catch (err) {
      // The backend reports real failures as {"error": "Failed To Send
      // Certificate: <cause>"}, but Client.js only builds its Error message from
      // `detail`/`message`, so every backend failure used to surface here as a
      // bare "500" with the cause thrown away. Read the payload directly.
      const payload = err?.payload || {};
      const backendError =
        payload.detail ||
        payload.error ||
        payload.email ||
        payload.recipient ||
        (typeof payload === "string" ? payload : "");
      setResult({
        ok: false,
        message: backendError || err?.message || "Failed To Send Certificate.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ padding: "24px 28px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            HR / Certificates
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            <Award size={26} /> Send Certificate
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="Soft-Button Small" onClick={doPreview} disabled={!certTemplate || !selectedEmployee}>
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
            {showPreview ? "Hide Preview" : "Preview"}
          </button>
          <button className="Primary-Button Small" onClick={sendCertificate} disabled={busy || !!validationError || repeatSend}>
            <Send size={14} /> {busy ? "Sending..." : repeatSend ? "Certificate Sent" : "Send Certificate"}
          </button>
        </div>
      </div>

      {result && (
        <div className={result.ok ? "Auth-AlertOk" : "Auth-Alert"} style={{ marginBottom: 16 }}>
          {result.message}
        </div>
      )}

      {!certTemplate && (
        <div style={{ padding: "12px 16px", background: "var(--red-50)", border: "2px solid var(--red-200)", borderRadius: 8, fontSize: 13, color: "var(--red-700)", marginBottom: 16 }}>
          No Certificate Template Found. Run <code>Seed_Offer_Templates</code> First.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: showPreview ? "1fr 500px" : "1fr", gap: 24 }}>
        {/* LEFT: Form */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, padding: 20 }}>
          <h2 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>Employee & Certificate Details</h2>

          <div className="Form-Grid Two" style={{ gap: 12 }}>
            {/* Employee Search */}
            <label style={{ gridColumn: "1/-1" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--c-700)" }}>Search Employee</span>
              <span style={{ position: "relative", display: "block" }}>
                <Search size={14} style={{ position: "absolute", left: 8, top: 9, opacity: 0.5 }} />
                <input
                  className="Mini-Inp"
                  style={{ paddingLeft: 26 }}
                  placeholder="Search By Name, Email, Or Username…"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSelectedEmployee(null); }}
                />
              </span>
            </label>
            <label style={{ gridColumn: "1/-1" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--c-700)" }}>Select Employee</span>
              <select
                className="Mini-Inp"
                value={selectedEmployee?.id || ""}
                onChange={(e) => handleSelectEmployee(e.target.value)}
              >
                <option value="">— Select Employee —</option>
                {filtered.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.display_name || emp.candidate_name || `#${emp.id}`}
                    {emp.candidate_email ? ` (${emp.candidate_email})` : ""}
                  </option>
                ))}
              </select>
            </label>

            {selectedEmployee && (
              <div style={{ gridColumn: "1/-1", padding: "8px 12px", background: "var(--green-50)", border: "1px solid var(--green-100)", borderRadius: 6, fontSize: 13, color: "var(--green-700)", marginBottom: 4 }}>
                Selected: <strong>{selectedEmployee.display_name || selectedEmployee.candidate_name}</strong>
                {selectedEmployee.candidate_email ? ` — ${selectedEmployee.candidate_email}` : ""}
                {selectedEmployee.position_title ? ` — ${selectedEmployee.position_title}` : ""}
              </div>
            )}

            <label>
              Joining Date
              <input type="date" className="Mini-Inp" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} />
            </label>
            <label>
              Completion Date
              <input type="date" className="Mini-Inp" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} />
            </label>
            <label style={{ gridColumn: "1/-1" }}>
              Position
              <input className="Mini-Inp" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. React Developer" />
            </label>
            <label style={{ gridColumn: "1/-1" }}>
              Major Responsibilities (If Any)
              <textarea
                className="Mini-Inp"
                style={{ minHeight: 60, resize: "vertical" }}
                value={responsibility}
                onChange={(e) => setResponsibility(e.target.value)}
                placeholder="Describe Key Responsibilities Or Achievements…"
              />
            </label>
          </div>
        </div>

        {/* RIGHT: Preview */}
        {showPreview && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "10px 16px", background: "var(--surface-soft)", borderBottom: "1px solid var(--c-200)", fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Certificate Preview</span>
              <button className="Soft-Button Small" style={{ fontSize: 11, padding: "4px 10px" }} onClick={doPreview}>
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            {previewHtml ? (
              <iframe sandbox="" srcDoc={previewHtml} title="Certificate Preview" style={{ width: "100%", height: "80vh", border: "none" }} />
            ) : (
              <div style={{ padding: 40, textAlign: "center", color: "var(--c-400)", fontSize: 14 }}>
                Select An Employee And Click "Preview"
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
